/**
 * Global React Query client — hardened, single source of truth.
 *
 * Before this file existed, `src/App.tsx` instantiated `new QueryClient(...)`
 * inline. That was fine for a single-consumer app, but:
 *   • It made it impossible to call `queryClient.invalidateQueries(...)` from
 *     non-React modules (service-worker bridges, WebSocket handlers, etc.)
 *     without drilling the instance down through React context.
 *   • There was no place to attach global cache-level error handlers — every
 *     failed query just died silently in the component that ran it.
 *   • The defaults (`staleTime: 5 min`, `refetchOnWindowFocus: true`) caused a
 *     thundering-herd of Supabase requests every time a user tabbed back into
 *     the app on a session that had been open for more than five minutes.
 *
 * This module fixes all three: a single exported `queryClient`, conservative
 * global defaults, and cache-level `onError` hooks ready to be upgraded to
 * Rollbar error reporting (see Pillar 2 of the SRE audit).
 */

import {
  QueryClient,
  QueryCache,
  MutationCache,
  isCancelledError,
  type Query,
  type Mutation,
} from "@tanstack/react-query";

import { reportToRollbar } from "./rollbarReport";
import {
  extractHttpStatus,
  queryRetryDelay,
  shouldRetryQuery,
} from "./queryErrorUtils";

/** “Expected” client failures (4xx) and TanStack cancels — skip Rollbar. */
function shouldReportReactQueryFailureToRollbar(error: unknown): boolean {
  if (isCancelledError(error)) return false;
  const s = extractHttpStatus(error);
  if (s !== null && s >= 400 && s < 500) return false;
  return true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cache-level error handlers
// ─────────────────────────────────────────────────────────────────────────────
//
// Fires after retries are exhausted. Always `console.error`; forwards to
// Rollbar for unexpected failures (see `shouldReportReactQueryFailureToRollbar`).

function logQueryError(error: unknown, query: Query): void {
  console.error(
    "[react-query] query failed",
    {
      queryKey: query.queryKey,
      queryHash: query.queryHash,
      status: extractHttpStatus(error),
      state: query.state.status,
    },
    error,
  );
  if (shouldReportReactQueryFailureToRollbar(error)) {
    reportToRollbar(error, {
      tags:   { "rq-layer": "query" },
      extra: {
        queryKey: JSON.stringify(query.queryKey),
        queryHash: query.queryHash,
        status: extractHttpStatus(error) ?? "unknown",
      },
    });
  }
}

function logMutationError(
  error: unknown,
  _variables: unknown,
  _context: unknown,
  mutation: Mutation<unknown, unknown, unknown, unknown>,
): void {
  console.error(
    "[react-query] mutation failed",
    {
      mutationKey: mutation.options.mutationKey,
      status: extractHttpStatus(error),
    },
    error,
  );
  if (shouldReportReactQueryFailureToRollbar(error)) {
    const mk = mutation.options.mutationKey;
    reportToRollbar(error, {
      tags:   { "rq-layer": "mutation" },
      extra: {
        mutationKey: mk === undefined ? "none" : JSON.stringify(mk),
        status: extractHttpStatus(error) ?? "unknown",
      },
    });
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// The singleton
// ─────────────────────────────────────────────────────────────────────────────

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: logQueryError,
  }),
  mutationCache: new MutationCache({
    onError: logMutationError,
  }),
  defaultOptions: {
    queries: {
      // 5 minutes of freshness. Background refetches on focus/mount/reconnect
      // become no-ops when data is still fresh, which is what kills the
      // "refetch hell" every time a user tabs away and comes back. Anything
      // time-sensitive (notifications feed, realtime-driven lists) is
      // already invalidated explicitly from `RealtimeSync` — we do NOT need
      // a blind refetch on every micro-interaction.
      staleTime: 5 * 60_000,
      // 10 minutes of in-memory retention after all observers unmount. Back
      // / forward navigation, and quick route tab-switching, now hit warm
      // cache instead of the network.
      gcTime: 10 * 60_000,
      // Hard off globally. Individual screens that legitimately need it can
      // opt in locally, but the default is: if you already have data, show
      // it — never block the UI for a background poll.
      refetchOnWindowFocus: false,
      // React Query already falls back to cached data while a background
      // refetch is in flight — combined with the 5-minute staleTime this
      // means route-switching hits warm cache and only fires a silent
      // background refetch when the data is actually stale.
      refetchOnReconnect: true,
      // Retry transient errors with exponential backoff (max 3 attempts).
      retry: shouldRetryQuery,
      retryDelay: queryRetryDelay,
    },
    mutations: {
      // Never retry mutations automatically — mutation payloads are not
      // inherently idempotent. Retrying a failed POST /events can silently
      // create duplicates; retrying a failed RPC can double-charge an
      // optimistic counter. Consumers that want retries can opt in locally.
      retry: false,
    },
  },
});
